import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import httpClient from '../api/axios';
import { useNavigate } from 'react-router-dom';

const ContactPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const navigate = useNavigate();

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    message: '',
  });

  // Background animation state
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset form status
    setFormStatus({
      submitted: false,
      error: false,
      message: '',
    });

    try {
      const response = await httpClient.post('/api/contact', {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });
      console.log(response.data);
      // Handle successful submission
      setFormStatus({
        submitted: true,
        error: false,
        message:
          'Thank you for your message! We will get back to you shortly.',
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      let errorMessage =
        'An error occurred while submitting your message. Please try again.';

      if (error.response) {
        // The request was made and the server responded with a status code
        if (error.response.status === 400) {
          errorMessage =
            error.response.data.message ||
            'Please check your form inputs and try again.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage =
          'No response from server. Please check your connection.';
      }

      setFormStatus({
        submitted: true,
        error: true,
        message: errorMessage,
      });
    }
  };
  // Enhanced animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const mapIn = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.8 },
    },
  };

  // New animation variants
  const heroTextVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        delay: 0.2,
        ease: [0.65, 0, 0.35, 1],
      },
    },
  };

  const flowingGrid = {
    animate: {
      backgroundPosition: ['0px 0px', '100px 100px'],
      transition: {
        duration: 10,
        ease: 'linear',
        repeat: Infinity,
      },
    },
  };

  const pulseVariants = {
    initial: { scale: 1 },
    animate: {
      scale: [1, 1.05, 1],
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: 'loop',
      },
    },
  };

  const floatVariants = {
    initial: { y: 0 },
    animate: {
      y: [0, -10, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        repeatType: 'loop',
        ease: 'easeInOut',
      },
    },
  };

  const buttonVariants = {
    initial: { scale: 1 },
    hover: {
      scale: 1.05,
      backgroundColor: '#166534', // darker green
      transition: { duration: 0.3 },
    },
    tap: { scale: 0.95 },
  };

  const iconVariants = {
    initial: { rotate: 0 },
    hover: {
      rotate: 360,
      transition: { duration: 0.5 },
    },
  };

  const socialIconVariants = {
    initial: { scale: 1, rotate: 0 },
    hover: {
      scale: 1.2,
      rotate: 15,
      transition: { duration: 0.3 },
    },
    tap: { scale: 0.9 },
  };

  const inputFocusVariants = {
    initial: { borderColor: '#E5E7EB' }, // gray-300
    focus: {
      borderColor: '#10B981', // green-500
      boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.2)',
      transition: { duration: 0.2 },
    },
  };

  return (
    <div className='bg-gray-50 min-h-screen pt-24 pb-16 overflow-hidden'>
      {/* Hero Section with enhanced animations */}
      <motion.section
        className='relative bg-green-600 text-white mt-8 py-16 md:py-24 overflow-hidden'
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        style={{
          backgroundImage:
            "url('https://img.freepik.com/free-photo/stashed-flour-used-cooking_23-2149517214.jpg?t=st=1745906171~exp=1745909771~hmac=583d3f5576684161ff0857964b138d95e5f31831559463fcea2b9b59d8e5af7b&w=740')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className='absolute inset-0 overflow-hidden'>
          <div className='absolute inset-0 bg-black opacity-30'></div>

          {/* Animated background pattern */}
          <motion.div
            className='absolute inset-0 opacity-10'
            animate={{
              backgroundPosition: [`0px 0px`, `100px 100px`],
            }}
            transition={{
              duration: 20,
              ease: 'linear',
              repeat: Infinity,
            }}
            style={{
              backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.2) 1px, transparent 1px)`,
              backgroundSize: `30px 30px`,
            }}
          />

          {/* Animated grain effect */}
          <motion.div
            className='absolute inset-0 mix-blend-overlay opacity-20'
            animate={{
              backgroundPosition: [`0% 0%`, `100% 100%`],
            }}
            transition={{
              duration: 8,
              ease: 'linear',
              repeat: Infinity,
              repeatType: 'reverse',
            }}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
            }}
          />

          {/* Mouse follow light effect */}
          <div
            className='absolute opacity-30 rounded-full blur-3xl bg-white w-64 h-64 pointer-events-none'
            style={{
              left: mousePosition.x - 128,
              top: mousePosition.y - 128,
              transform: `translate(calc(${
                -mousePosition.x / 20
              }px), calc(${-mousePosition.y / 20}px))`,
              transition: 'transform 0.1s ease-out',
            }}
          />
        </div>

        <div className='container mx-auto px-4 relative z-10'>
          <motion.div
            variants={heroTextVariants}
            initial='hidden'
            animate='visible'
            className='max-w-3xl mx-auto text-center'
          >
            <motion.h1
              className='text-4xl md:text-5xl lg:text-6xl font-bold mb-6'
              animate={{
                textShadow: [
                  '0 0 4px rgba(255,255,255,0.1)',
                  '0 0 8px rgba(255,255,255,0.3)',
                  '0 0 4px rgba(255,255,255,0.1)',
                ],
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              Get In Touch
            </motion.h1>

            <motion.p
              className='text-lg md:text-xl opacity-90'
              variants={floatVariants}
              initial='initial'
              animate='animate'
            >
              Have questions about our products or services? We'd love
              to hear from you!
            </motion.p>

            {/* Animated decorative elements */}
            <motion.div
              className='absolute -bottom-6 left-1/2 transform -translate-x-1/2'
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.6 }}
            >
              <motion.div
                className='w-16 h-1 bg-white rounded-full mx-auto'
                animate={{ scaleX: [1, 1.5, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Contact Form Section with enhanced animations */}
      <section className='py-16 md:py-24 relative'>
        {/* Background decorative elements */}
        <motion.div
          className='absolute top-0 right-0 w-64 h-64 bg-green-100 rounded-full opacity-30 -z-10'
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 0.3 }}
          transition={{ duration: 1 }}
          style={{ filter: 'blur(80px)' }}
        />

        <motion.div
          className='absolute bottom-20 left-10 w-48 h-48 bg-green-200 rounded-full opacity-20 -z-10'
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 0.2 }}
          transition={{ duration: 1, delay: 0.3 }}
          style={{ filter: 'blur(60px)' }}
        />

        <div className='container mx-auto px-4'>
          <motion.div
            className='grid grid-cols-1 lg:grid-cols-2 gap-12 items-start'
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerChildren}
          >
            {/* Contact Form with enhanced animations */}
            <motion.div
              className='bg-white rounded-lg shadow-lg p-8 md:p-10 relative overflow-hidden'
              variants={fadeIn}
              whileHover={{
                boxShadow:
                  '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              }}
              transition={{ duration: 0.3 }}
            >
              {/* Decorative elements */}
              <div className='absolute top-0 right-0 w-32 h-32 bg-green-50 rounded-bl-full -z-10'></div>
              <div className='absolute bottom-0 left-0 w-16 h-16 bg-green-50 rounded-tr-full -z-10'></div>

              <motion.h2
                className='text-3xl font-bold text-gray-800 mb-6 relative'
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                Send Us a Message
                <motion.span
                  className='absolute -bottom-1 left-0 h-1 bg-green-500 rounded-full'
                  initial={{ width: 0 }}
                  whileInView={{ width: '60px' }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  viewport={{ once: true }}
                />
              </motion.h2>

              <AnimatePresence>
                {formStatus.submitted ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                    className={`p-6 rounded-lg mb-6 ${
                      formStatus.error
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                    }`}
                  >
                    <div className='flex items-center'>
                      <div className='mr-4 bg-white rounded-full p-2'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          className='h-6 w-6 text-green-600'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M5 13l4 4L19 7'
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className='font-bold text-lg mb-1'>
                          Message Sent!
                        </h3>
                        <p>{formStatus.message}</p>
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.form
                    onSubmit={handleSubmit}
                    initial='hidden'
                    animate='visible'
                    variants={{
                      hidden: { opacity: 0 },
                      visible: {
                        opacity: 1,
                        transition: {
                          staggerChildren: 0.15,
                        },
                      },
                    }}
                  >
                    <motion.div className='mb-6' variants={fadeIn}>
                      <label
                        htmlFor='name'
                        className='block text-gray-700 font-medium mb-2'
                      >
                        Your Name
                      </label>
                      <motion.input
                        type='text'
                        id='name'
                        name='name'
                        value={formData.name}
                        onChange={handleChange}
                        required
                        initial={{ borderColor: '#E5E7EB' }}
                        whileFocus={{
                          borderColor: '#10B981',
                          boxShadow:
                            '0 0 0 3px rgba(16, 185, 129, 0.2)',
                        }}
                        className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none transition-all duration-300'
                        placeholder='Enter your name'
                      />
                    </motion.div>

                    <motion.div className='mb-6' variants={fadeIn}>
                      <label
                        htmlFor='email'
                        className='block text-gray-700 font-medium mb-2'
                      >
                        Email Address
                      </label>
                      <motion.input
                        type='email'
                        id='email'
                        name='email'
                        value={formData.email}
                        onChange={handleChange}
                        required
                        initial={{ borderColor: '#E5E7EB' }}
                        whileFocus={{
                          borderColor: '#10B981',
                          boxShadow:
                            '0 0 0 3px rgba(16, 185, 129, 0.2)',
                        }}
                        className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none transition-all duration-300'
                        placeholder='Enter your email'
                      />
                    </motion.div>

                    <motion.div className='mb-6' variants={fadeIn}>
                      <label
                        htmlFor='subject'
                        className='block text-gray-700 font-medium mb-2'
                      >
                        Subject
                      </label>
                      <motion.input
                        type='text'
                        id='subject'
                        name='subject'
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        initial={{ borderColor: '#E5E7EB' }}
                        whileFocus={{
                          borderColor: '#10B981',
                          boxShadow:
                            '0 0 0 3px rgba(16, 185, 129, 0.2)',
                        }}
                        className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none transition-all duration-300'
                        placeholder='What is this regarding?'
                      />
                    </motion.div>

                    <motion.div className='mb-6' variants={fadeIn}>
                      <label
                        htmlFor='message'
                        className='block text-gray-700 font-medium mb-2'
                      >
                        Your Message
                      </label>
                      <motion.textarea
                        id='message'
                        name='message'
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows='5'
                        initial={{ borderColor: '#E5E7EB' }}
                        whileFocus={{
                          borderColor: '#10B981',
                          boxShadow:
                            '0 0 0 3px rgba(16, 185, 129, 0.2)',
                        }}
                        className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none transition-all duration-300'
                        placeholder='Tell us what you need...'
                      />
                    </motion.div>

                    <motion.button
                      type='submit'
                      className='w-full bg-green-600 text-white py-3 px-6 rounded-lg font-medium focus:outline-none'
                      variants={buttonVariants}
                      initial='initial'
                      whileHover='hover'
                      whileTap='tap'
                    >
                      <span className='flex items-center justify-center'>
                        <span>Send Message</span>
                        <motion.span
                          className='ml-2'
                          variants={iconVariants}
                          initial='initial'
                          whileHover='hover'
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='h-5 w-5'
                            viewBox='0 0 20 20'
                            fill='currentColor'
                          >
                            <path
                              fillRule='evenodd'
                              d='M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z'
                              clipRule='evenodd'
                            />
                          </svg>
                        </motion.span>
                      </span>
                    </motion.button>
                  </motion.form>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Contact Information with enhanced animations */}
            <div>
              <motion.div
                className='bg-white rounded-lg shadow-lg p-8 md:p-10 mb-8 relative'
                variants={fadeIn}
                whileHover={{
                  boxShadow:
                    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                }}
                transition={{ duration: 0.3 }}
              >
                {/* Decorative element */}
                <div className='absolute -top-4 -right-4 w-20 h-20 bg-green-50 rounded-full -z-10'></div>

                <motion.h2
                  className='text-3xl font-bold text-gray-800 mb-6 relative'
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  Contact Information
                  <motion.span
                    className='absolute -bottom-1 left-0 h-1 bg-green-500 rounded-full'
                    initial={{ width: 0 }}
                    whileInView={{ width: '60px' }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                    viewport={{ once: true }}
                  />
                </motion.h2>

                <motion.div
                  className='space-y-6'
                  variants={{
                    hidden: { opacity: 0 },
                    visible: {
                      opacity: 1,
                      transition: {
                        staggerChildren: 0.15,
                      },
                    },
                  }}
                  initial='hidden'
                  whileInView='visible'
                  viewport={{ once: true }}
                >
                  {[
                    {
                      icon: (
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          className='h-6 w-6 text-green-600'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                          />
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                          />
                        </svg>
                      ),
                      title: 'Address',
                      content: (
                        <>
                          Godrej Green Cove, Tower No 3, Flat no - 905{' '}
                          <br />
                          Nande Balewadi Road, Mahalunge, Pune 411045
                        </>
                      ),
                    },
                    {
                      icon: (
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          className='h-6 w-6 text-green-600'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z'
                          />
                        </svg>
                      ),
                      title: 'Phone',
                      content: <>Customer Service: 9765660423</>,
                    },
                    {
                      icon: (
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          className='h-6 w-6 text-green-600'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                          />
                        </svg>
                      ),
                      title: 'Email',
                      content: (
                        <>Customer Support: pralhadflours@gmail.com</>
                      ),
                    },
                    {
                      icon: (
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          className='h-6 w-6 text-green-600'
                          fill='none'
                          viewBox='0 0 24 24'
                          stroke='currentColor'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                          />
                        </svg>
                      ),
                      title: 'Business Hours',
                      content: (
                        <>
                          Monday - Friday: 9:00 AM - 6:00 PM
                          <br />
                          Saturday: 10:00 AM - 4:00 PM
                          <br />
                          Sunday: Closed
                        </>
                      ),
                    },
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      className='flex items-start group'
                      variants={fadeIn}
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <motion.div
                        className='flex-shrink-0 bg-green-100 rounded-full p-3 mr-4 group-hover:bg-green-200'
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.3 }}
                        variants={pulseVariants}
                        initial='initial'
                        animate='animate'
                      >
                        {item.icon}
                      </motion.div>
                      <div>
                        <h3 className='text-lg font-semibold text-gray-800 mb-1 group-hover:text-green-600 transition-colors duration-200'>
                          {item.title}
                        </h3>
                        <p className='text-gray-600'>
                          {item.content}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
              <iframe
                className='w-[728px] h-[300px]'
                src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4486.406330108387!2d73.73298907579773!3d18.566209967777404!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2bd1d5b57fe31%3A0x900885e0d00e75cb!2sGodrej%20Green%20Cove%2C%20Mahalunge%2C%20Pune!5e1!3m2!1sen!2sin!4v1746533032200!5m2!1sen!2sin'
                allowfullscreen=''
                loading='lazy'
                referrerpolicy='no-referrer-when-downgrade'
              ></iframe>
            </div>
          </motion.div>
        </div>
      </section>

      <motion.section
        className='py-16 bg-green-600 text-white relative overflow-hidden'
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        {/* Animated background elements */}
        <div className='absolute inset-0 overflow-hidden'>
          <motion.div
            className='absolute inset-0 opacity-10'
            animate={{
              backgroundPosition: [`0px 0px`, `100px 100px`],
            }}
            transition={{
              duration: 20,
              ease: 'linear',
              repeat: Infinity,
            }}
            style={{
              backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.3) 1px, transparent 1px)`,
              backgroundSize: `30px 30px`,
            }}
          />
        </div>

        <div className='container mx-auto px-4 relative z-10'>
          <motion.div
            className='max-w-3xl mx-auto text-center'
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <motion.h2
              className='text-3xl md:text-4xl font-bold mb-6'
              variants={pulseVariants}
              initial='initial'
              animate='animate'
            >
              Ready to Experience Pralhad Flours?
            </motion.h2>
            <p className='text-lg opacity-90 mb-8'>
              Visit our store or shop online to discover our premium
              selection of flours and baking ingredients.
            </p>
            <div className='flex flex-col sm:flex-row gap-4 justify-center'>
              <motion.a
                className='bg-white text-green-600 px-8 py-3 rounded-lg font-medium shadow-lg cursor-pointer'
                variants={buttonVariants}
                initial='initial'
                whileHover='hover'
                whileTap='tap'
                onClick={() => navigate('/products')}
              >
                Shop Now
              </motion.a>
              <motion.a
                onClick={() => navigate('/products')}
                className='bg-transparent border-2 border-white  cursor-pointer text-white px-8 py-3 rounded-lg font-medium hover:bg-white hover:text-green-600 transition-colors duration-300'
                whileHover={{
                  scale: 1.05,
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  color: '#047857',
                }}
                whileTap={{ scale: 0.95 }}
              >
                Find a Store
              </motion.a>
            </div>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
};

export default ContactPage;
